import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import dayjs from 'dayjs';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

import {
  ConfirmationActionModalComponent,
  ConfirmationActionModalData,
} from '@shared/components/modals/confirmation-action-modal/confirmation-action-modal.components';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import {
  ApprovalType,
  EntityType,
  EventType,
  GqlService,
  InvoiceStatus,
  NoteType,
  UpdateInvoiceInput,
  UpdateWorkflowInput,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { EventService } from '@shared/store/event/event.service';
import { InvoiceModel } from '@shared/store/invoice/invoice.model';
import { InvoiceStore } from '@shared/store/invoice/invoice.store';
import { batchAPIRequest, batchPromises } from '@shared/utils';
import { Utils } from '@shared/utils/utils';

import { IInvoicesGridData } from './invoices.component';
import { InvoiceIntegrationLineItem } from './invoices.model';
import type { PeriodCloseComponent } from '../../../period-close.component';

@Injectable({
  providedIn: 'root',
})
export class InvoicesPageService {
  eventService = inject(EventService);
  gqlService = inject(GqlService);
  invoiceStore = inject(InvoiceStore);
  overlayService = inject(OverlayService);
  router = inject(Router);

  expandableIntegrationLineItems$ = new BehaviorSubject<InvoiceIntegrationLineItem[]>([]);

  // eslint-disable-next-line max-lines-per-function
  async batchSaveInvoices({
    changedRows,
    checkIfInvoiceStatusChanged,
    getInvoiceStatusChangeReason,
    initialValues,
    isNewPrepaidImpactRows = false,
    periodCloseComponent,
    rowsToDelete,
    shouldShowMarkedAsUnpaidModal,
    successSaveModalMessage,
    vendorIdToNavigate,
  }: {
    changedRows: IInvoicesGridData[];
    checkIfInvoiceStatusChanged: (invoice: IInvoicesGridData) => boolean;
    getInvoiceStatusChangeReason: (invoice: InvoiceModel, status: InvoiceStatus) => null | string;
    initialValues: IInvoicesGridData[];
    isNewPrepaidImpactRows?: boolean;
    periodCloseComponent: PeriodCloseComponent;
    rowsToDelete: { invoice: InvoiceModel; reason: string }[];
    shouldShowMarkedAsUnpaidModal: boolean;
    successSaveModalMessage: string;
    vendorIdToNavigate: string;
  }) {
    const apiErrors: string[] = [];
    const mapError = (result: boolean | Error | { errors: string[]; success: boolean }) => {
      if (result instanceof Error) {
        apiErrors.push(result.message);
      } else if (typeof result === 'object' && result?.success) {
        apiErrors.push(...result.errors);
      }
    };

    if (rowsToDelete.length) {
      const deleteResults = await batchPromises(
        rowsToDelete,
        ({ invoice, reason }) => {
          return this.remove(invoice, reason);
        },
        5
      );

      deleteResults.forEach(mapError);
    }

    if (changedRows.length) {
      const invoicesWithApproveRules = changedRows.filter(
        (invoice) =>
          [InvoiceStatus.STATUS_APPROVED, InvoiceStatus.STATUS_DECLINED].includes(
            invoice.invoice_status
          ) && checkIfInvoiceStatusChanged(invoice)
      );

      const approveResults = await batchPromises(
        invoicesWithApproveRules,
        ({ id, invoice_status }) => {
          return firstValueFrom(
            this.gqlService.approveRule$({
              activity_details: '{}',
              approval_type: ApprovalType.APPROVAL_INVOICE,
              approved: invoice_status === InvoiceStatus.STATUS_APPROVED,
              comments: '',
              entity_id: id,
              entity_type: EntityType.INVOICE,
              permission: 'PERMISSION_APPROVE_INVOICE',
            })
          );
        },
        5
      );

      approveResults.forEach(mapError);

      await this.batchUpdate(
        changedRows.map((invoice) => {
          let formatted_previous_services_period = null;
          const previous_services_period = initialValues.find(
            (value) => value.id === invoice.id
          )?.services_period;

          if (previous_services_period) {
            formatted_previous_services_period =
              dayjs(previous_services_period).format('YYYY-MM-DD');
          }

          return {
            ...invoice,
            accrual_period: invoice.accrual_period
              ? dayjs(invoice.accrual_period).format('YYYY-MM-DD')
              : null,
            admin_review_reason: getInvoiceStatusChangeReason(
              invoice,
              InvoiceStatus.STATUS_PENDING_REVIEW
            ),
            decline_reason: getInvoiceStatusChangeReason(invoice, InvoiceStatus.STATUS_DECLINED),
            due_date: invoice.due_date || null,
            invoice_date: invoice.invoice_date || null,
            payment_date: invoice.payment_date || null,
            po_reference: invoice.po_reference || null,
            previous_services_period: formatted_previous_services_period,
            services_period: invoice.services_period
              ? dayjs(invoice.services_period).format('YYYY-MM-DD')
              : null,
          };
        }),
        false
      );
    }

    if (apiErrors.length) {
      this.overlayService.error(MessagesConstants.SOME_CHANGES_ARE_NOT_SAVED);
    } else {
      this.overlayService.success(MessagesConstants.SUCCESSFULLY_SAVED);

      if (isNewPrepaidImpactRows || shouldShowMarkedAsUnpaidModal) {
        void this.goToPrepaidsInEditModeIfUserAccepts({
          periodCloseComponent,
          successSaveModalMessage,
          vendorId: vendorIdToNavigate,
        });
      }
    }
  }

  // eslint-disable-next-line max-lines-per-function
  async batchUpdate(invoices: InvoiceModel[], showSuccessMessages = true, setLoading = true) {
    const invoiceMaxBatchSize = 50;
    const workflowMaxBatchSize = 200;

    if (setLoading) {
      this.invoiceStore.setLoading(true);
    }

    const workflowInputs: UpdateWorkflowInput[] = [];

    invoices.forEach((invoice) => {
      invoice.cards.forEach((card) => {
        const { header, id, lines, note, status } = card;

        workflowInputs.push({
          id,
          properties: JSON.stringify({ properties: { header, lines, note, status } }),
        });
      });
    });

    const workflowResponses = await batchAPIRequest(
      workflowInputs,
      this.gqlService.batchUpdateWorkflowDetails$.bind(this),
      workflowMaxBatchSize
    );

    // check if everything is ok
    if (workflowResponses.success) {
      const invoiceInputs: UpdateInvoiceInput[] = [];

      invoices.forEach((invoice) => {
        invoiceInputs.push({
          accrual_period: invoice.accrual_period,
          admin_review_reason: invoice.admin_review_reason,
          decline_reason: invoice.decline_reason,
          discount_amount: invoice.expense_amounts.discount_total?.value || 0,
          due_date: invoice.due_date,
          has_prepaid: invoice.has_prepaid,
          id: invoice.id,
          investigator_amount: invoice.expense_amounts.investigator_total?.value || 0,
          invoice_date: invoice.invoice_date,
          invoice_no: invoice.invoice_no,
          invoice_status: invoice.invoice_status,
          is_deposit: invoice.is_deposit,
          organization_id: invoice.organization.id,
          passthrough_amount: invoice.expense_amounts.pass_thru_total?.value || 0,
          payment_date: invoice.payment_date,
          payment_status: invoice.payment_status,
          po_reference: invoice.po_reference,
          previous_services_period: invoice.previous_services_period,
          service_amount: invoice.expense_amounts.services_total?.value || 0,
          services_period: invoice.services_period,
          total_amount: invoice.expense_amounts.invoice_total?.value || 0,
        });
      });

      const invoiceResponses = await batchAPIRequest(
        invoiceInputs,
        this.gqlService.batchUpdateInvoices$.bind(this),
        invoiceMaxBatchSize
      );

      if (invoiceResponses.success && invoiceResponses.data && invoiceResponses.data.length > 0) {
        if (showSuccessMessages) {
          this.overlayService.success(MessagesConstants.INVOICE.SUCCESSFULLY_UPDATED);
        }

        invoices.forEach((invoice) => {
          const invoiceData = invoiceResponses.data?.find((datum) => datum.id === invoice.id);

          this.invoiceStore.update(
            invoice.id,
            () =>
              ({
                ...invoiceData,
                cards: invoice.cards,
                decline_reason: invoice.decline_reason,
                expense_amounts: invoice.expense_amounts,
                payment_date: invoice.payment_date ?? null,
                po_reference: invoiceData?.purchase_order_id || '',
              }) as unknown as InvoiceModel
          );
        });

        if (setLoading) {
          this.invoiceStore.setLoading(false);
        }

        return true;
      }

      this.overlayService.error(invoiceResponses.errors);
    }

    this.overlayService.error(workflowResponses.errors);

    if (setLoading) {
      this.invoiceStore.setLoading(false);
    }

    return false;
  }

  /**
   * Waits until periodCloseComponent.runningOnInit is false, checking every 100ms.
   * Resolves immediately if already false.
   */
  async checkIfParentIsLoading(periodCloseComponent: PeriodCloseComponent): Promise<void> {
    if (!periodCloseComponent.runningOnInit) {
      return;
    }

    await new Promise<void>((resolve) => {
      const interval = setInterval(() => {
        if (!periodCloseComponent.runningOnInit) {
          clearInterval(interval);
          resolve();
        }
      }, 100);
    });
  }

  async goToPrepaidsInEditModeIfUserAccepts({
    periodCloseComponent,
    successSaveModalMessage,
    vendorId,
  }: {
    periodCloseComponent: PeriodCloseComponent;
    successSaveModalMessage: string;
    vendorId: string;
  }) {
    let isEventCome = false;

    // after we update the invoices we are getting a new event
    // but we are showing the modal before the event is emitted
    // and we shouldn't wait for the event to be emitted to show the modal
    // so we are subscribing to the event and showing the modal
    const eventSubscription = this.eventService
      .select$(EventType.UPDATE_QUARTER_ACCRUALS)
      .subscribe(() => {
        isEventCome = true;
      });

    const resp = this.overlayService.openPopup<
      ConfirmationActionModalData,
      boolean,
      ConfirmationActionModalComponent
    >({
      data: {
        hideConfirmationHint: true,
        message: successSaveModalMessage,
        skipKeywordConfirmation: true,
      },
      modal: ConfirmationActionModalComponent,
      settings: {
        header: 'Adjust Prepaids?',
        primaryButton: {
          label: 'Adjust Prepaids',
        },
        secondaryButton: {
          label: 'Close',
        },
      },
    });

    const event = await firstValueFrom(resp.afterClosed$);

    if (event.data) {
      // after the modal is closed we are checking if the event was emitted
      if (!isEventCome) {
        eventSubscription.unsubscribe();
        // if the event was not emitted we are waiting for it
        await firstValueFrom(this.eventService.select$(EventType.UPDATE_QUARTER_ACCRUALS));
      }

      // after the event is emitted we are checking if the parent is loading
      // because the parent has a fixed 5000ms timeout for the loading
      await this.checkIfParentIsLoading(periodCloseComponent);

      // after the parent is not loading we are navigating to the prepaids page
      void this.router.navigate(
        [
          `/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.INVOICES_AND_PREPAIDS.INDEX}/${ROUTING_PATH.CLOSING.INVOICES_AND_PREPAIDS.PREPAIDS}/${vendorId}`,
        ],
        { queryParams: { editMode: true }, queryParamsHandling: 'merge' }
      );
    }

    eventSubscription.unsubscribe();
  }

  async remove(invoice: InvoiceModel, delete_reason = '') {
    const { errors, success } = await firstValueFrom(
      this.gqlService.removeInvoice$({ id: invoice.id })
    );

    if (success) {
      if (delete_reason.length > 0) {
        const { errors: cErrors, success: cSuccess } = await firstValueFrom(
          this.gqlService.createNote$({
            entity_id: invoice.id,
            entity_type: EntityType.INVOICE,
            message: Utils.scrubUserInput(delete_reason),
            note_type: NoteType.NOTE_TYPE_DELETE_REASON,
          })
        );

        if (!cSuccess) {
          this.overlayService.error(cErrors);
        }
      }

      this.invoiceStore.remove(invoice.id);
    } else {
      this.overlayService.error(errors);
    }

    return { errors, success };
  }
}
